import { useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, InputGroup, InputGroupText, FormFeedback,
    Button, Spinner, Row, Col, Alert,
} from 'reactstrap';
import { BiDownload, BiErrorCircle, BiGitBranch, BiCopyAlt, BiLockAlt } from 'react-icons/bi';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';
import useReceiptsByExpense from '../../hooks/useReceiptsByExpense';
import useUpdateExpense from '../../hooks/useUpdateExpense';
import useCategories, { CATEGORY_TARGET } from '../../hooks/useCategories';
import { fetchReceiptDownloadUrl } from '../../api/receiptsApi';
import { checkExpenseDuplicates, dismissExpenseDuplicate } from '../../api/expensesApi';
import AccountingSyncBadge from '../integrations/AccountingSyncBadge';
import DuplicateWarning from '../common/DuplicateWarning';
import { currencySymbol, CURRENCIES } from '../../constants/currencies';
import ExpenseSplitEditor, {
    splitsToPayload, splitsHaveErrors, splitsFromServer,
} from './expenseSplitEditor';

// Same threshold as the row-level pill — kept duplicated rather than imported
// because lifting a single literal out of two call-sites isn't worth a shared
// util yet.
const CONFIDENCE_FLOOR = 0.85;
const isLowConf = (conf, key) =>
    conf != null && conf[key] != null && conf[key] < CONFIDENCE_FLOOR;

function validateExpense({ amount, vatAmount, expenseDate }) {
    const errors = {};
    if (!expenseDate)                    errors.expenseDate = 'Expense date is required.';
    if (amount === '' || amount == null) errors.amount      = 'Amount is required.';
    else if (Number.isNaN(Number(amount)) || Number(amount) < 0)
                                         errors.amount      = 'Amount must be zero or positive.';
    if (vatAmount !== '' && vatAmount != null) {
        if (Number.isNaN(Number(vatAmount)) || Number(vatAmount) < 0)
            errors.vatAmount = 'VAT amount must be zero or positive.';
    }
    return { errors, hasErrors: Object.keys(errors).length > 0 };
}

const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// Two-pane "view + edit" modal for a single expense.
//   Left:  inline preview of the attached receipt (img for image content-types,
//          iframe for PDFs). Receipts are 1:1 with expenses in this UX, so we
//          render the first row and ignore any historical extras.
//   Right: editable form mirroring the create-expense fields. Save calls
//          UpdateExpense, which also clears OcrFieldConfidence server-side so
//          the "Please check" pill drops once the user has reviewed.
export default function ExpenseDetailModal({ isOpen, onClose, expense, currency = 'GBP' }) {
    const updateExpense = useUpdateExpense();
    const receipts = useReceiptsByExpense(isOpen ? expense?.id : null);
    const receipt = receipts.data?.[0] ?? null;
    const categories = useCategories(CATEGORY_TARGET.Expense);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [expenseDate, setExpenseDate] = useState(todayLocal());
    const [supplier, setSupplier]       = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory]       = useState('');
    const [amount, setAmount]           = useState('');
    const [vatAmount, setVatAmount]     = useState('');
    const [currencyValue, setCurrencyValue] = useState(currency);
    const [splits, setSplits]           = useState([]);
    const [attempted, setAttempted]     = useState(false);
    const [dupMatches, setDupMatches]   = useState([]);
    const [dupChecking, setDupChecking] = useState(false);
    // OCR-set flag on the row itself (vs. dupMatches, which is the live
    // pre-save check). Seeded from the expense; cleared on explicit dismiss.
    const [dupFlag, setDupFlag]         = useState(false);
    const [dismissing, setDismissing]   = useState(false);
    const [locked, setLocked]           = useState(false);

    const { errors, hasErrors } = validateExpense({ amount, vatAmount, expenseDate });

    // Re-seed the form whenever the modal (re)opens with a different expense.
    // Using useState's initial-only seed would leave stale values in the inputs
    // when the user closes one row and opens another.
    useEffect(() => {
        if (!isOpen || !expense) return;
        setExpenseDate(expense.expenseDate ?? todayLocal());
        setSupplier(expense.supplier ?? '');
        setDescription(expense.description ?? '');
        setCategory(expense.categoryId != null ? String(expense.categoryId) : '');
        setAmount(expense.amount != null ? Number(expense.amount).toFixed(2) : '');
        setVatAmount(expense.vatAmount != null ? Number(expense.vatAmount).toFixed(2) : '');
        setCurrencyValue(expense.currency ?? currency);
        setSplits(splitsFromServer(expense.splits, expense.amount));
        setAttempted(false);
        setDupMatches([]);
        setDupChecking(false);
        setDupFlag(Boolean(expense.possibleDuplicate));
        setLocked(false);
    }, [isOpen, expense]);

    const handleDismissDuplicate = async () => {
        if (!expense?.id) return;
        setDismissing(true);
        try {
            await dismissExpenseDuplicate(expense.id);
            setDupFlag(false);
            toast.success('Duplicate flag cleared');
        } catch (err) {
            toast.error(err?.message ?? 'Failed to clear duplicate flag');
        } finally {
            setDismissing(false);
        }
    };

    // Mint an inline-disposition SAS URL once we know the receipt id. Reset
    // when the modal closes so reopening a different expense doesn't briefly
    // flash the previous receipt — and refetches a fresh URL because the SAS
    // is short-lived (5 min TTL on the server).
    useEffect(() => {
        if (!isOpen || !receipt?.id) {
            setPreviewUrl(null);
            setPreviewError(null);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        fetchReceiptDownloadUrl(receipt.id, { inline: true })
            .then(({ url }) => { if (!cancelled) setPreviewUrl(url); })
            .catch(err => { if (!cancelled) setPreviewError(err?.message ?? 'Failed to load preview'); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, receipt?.id]);

    const handleClose = () => {
        if (updateExpense.isPending) return;
        onClose();
    };

    const handleDownload = async () => {
        if (!receipt) return;
        try {
            const { url } = await fetchReceiptDownloadUrl(receipt.id);
            window.location.href = url;
        } catch (err) {
            toast.error(err?.message ?? 'Failed to prepare download');
        }
    };

    const splitsInvalid = splitsHaveErrors(splits, amount);
    // null when collapsed = "leave splits unchanged"; [] = "clear all";
    // [...] = "replace with these". The editor returns null vs [] depending
    // on whether the user activated splits. To distinguish "user opened the
    // expense, never touched splits" from "user cleared the splits", we
    // compare against the original payload below.
    const splitsPayload = splitsToPayload(splits);
    const hadServerSplits = Array.isArray(expense?.splits) && expense.splits.length > 0;

    // First submit runs the duplicate check (excluding this row); matches show
    // the warning + "Save anyway". A failed check never blocks the save.
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (locked || updateExpense.isPending || dupChecking) return;
        if (hasErrors || splitsInvalid) { setAttempted(true); return; }

        setDupChecking(true);
        let matches = [];
        try {
            matches = await checkExpenseDuplicates({
                amount: Number(amount),
                date: expenseDate,
                supplier: supplier.trim() || undefined,
                excludeId: expense.id,
            });
        } catch { /* ignore — warning is advisory */ }
        setDupChecking(false);
        if (matches.length > 0) { setDupMatches(matches); return; }

        await doSave();
    };

    const doSave = async () => {
        if (updateExpense.isPending) return;
        // splitsPayload semantics:
        //  splits has 2+ rows  → [{...}] replace
        //  splits has 0 rows AND server had splits → [] clear
        //  splits has 0 rows AND server had none   → null leave alone
        const splitsField = splitsPayload != null
            ? splitsPayload
            : (hadServerSplits ? [] : null);

        await updateExpense.mutateAsync({
            id: expense.id,
            body: {
                expenseDate,
                supplier:    supplier.trim()    || null,
                description: description.trim() || null,
                categoryId:  category === '' ? null : Number(category),
                amount:      Number(amount),
                vatAmount:   vatAmount === '' ? null : Number(vatAmount),
                currency:    currencyValue,
                splits:      splitsField,
            },
        });
        onClose();
    };

    const ocrConf = expense?.ocrFieldConfidence;
    const lowConfFeedback = (key) =>
        isLowConf(ocrConf, key)
            ? <div className="text-warning small mt-1 d-inline-flex align-items-center gap-1">
                  <BiErrorCircle /> Detected with low confidence — please verify
              </div>
            : null;

    const isImage = receipt?.contentType?.startsWith('image/');
    const isPdf   = receipt?.contentType === 'application/pdf';

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="xl">
            <ModalHeader toggle={handleClose}>
                Expense details
                {receipt && (
                    <Button color="link" size="sm" className="ms-3 p-0 text-decoration-none"
                            onClick={handleDownload}
                            title="Download original">
                        <BiDownload className="me-1" /> Download
                    </Button>
                )}
            </ModalHeader>
            <Form onSubmit={handleSubmit} noValidate>
                <ModalBody>
                    <Row className="g-3">
                        {/* ---- Left: receipt preview ---- */}
                        <Col md={7}>
                            {Array.isArray(expense?.splits) && expense.splits.length > 0 && (
                                <Alert color="info" className="d-flex align-items-start gap-2 py-2 small mb-2">
                                    <BiGitBranch className="mt-1 flex-shrink-0" />
                                    <div>
                                        This receipt is shared across {expense.splits.length} jobs
                                        (total {formatCurrency(expense.amount, currencyValue)}).
                                        Edit the splits below to change each job's share.
                                    </div>
                                </Alert>
                            )}
                            <div className="border rounded bg-light d-flex align-items-center justify-content-center"
                                 style={{ minHeight: 480, height: '70vh', overflow: 'hidden' }}>
                                {receipts.isLoading ? (
                                    <Spinner />
                                ) : receipts.error ? (
                                    <div className="text-danger small p-3 text-center">
                                        Failed to load receipt.
                                    </div>
                                ) : !receipt ? (
                                    <div className="text-muted small p-3 text-center">
                                        No receipt attached to this expense.
                                    </div>
                                ) : previewLoading ? (
                                    <Spinner />
                                ) : previewError ? (
                                    <div className="text-danger small p-3 text-center">
                                        {previewError}
                                    </div>
                                ) : previewUrl && isImage ? (
                                    <img src={previewUrl}
                                         alt={receipt.originalFileName}
                                         style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : previewUrl && isPdf ? (
                                    // #navpanes=0 collapses Chromium's thumbnail
                                    // sidebar; #view=FitH fits the page to the
                                    // iframe width so the invoice is readable
                                    // without the user zooming first. The SAS
                                    // already uses `?`, so these live on the
                                    // fragment and don't disturb the signature.
                                    <iframe src={`${previewUrl}#view=FitH&navpanes=0`}
                                            title={receipt.originalFileName}
                                            className="w-100 h-100 border-0" />
                                ) : (
                                    <div className="text-muted small p-3 text-center">
                                        Preview not available for this file type.
                                        <div className="mt-2">
                                            <Button color="primary" size="sm" outline onClick={handleDownload}>
                                                <BiDownload className="me-1" /> Download instead
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {receipt && (
                                <div className="text-muted small mt-2 text-truncate">
                                    {receipt.originalFileName}
                                </div>
                            )}
                        </Col>

                        {/* ---- Right: editable form ---- */}
                        <Col md={5}>
                            {locked && (
                                <Alert color="secondary" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiLockAlt className="mt-1 flex-shrink-0" size={18} />
                                    <div>This expense is paid and locked in your accounting package, so it can’t be edited or deleted here.</div>
                                </Alert>
                            )}
                            <fieldset disabled={locked} className="border-0 p-0 m-0">
                            {dupFlag && (
                                <Alert color="warning" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiCopyAlt className="mt-1 flex-shrink-0" size={18} />
                                    <div className="flex-grow-1">
                                        This was flagged as a possible duplicate of an existing expense.
                                        Saving any edit clears this, or:
                                        <div className="mt-2">
                                            <Button color="warning" size="sm" outline
                                                    disabled={dismissing} onClick={handleDismissDuplicate}>
                                                {dismissing && <Spinner size="sm" className="me-1" />}
                                                Not a duplicate
                                            </Button>
                                        </div>
                                    </div>
                                </Alert>
                            )}
                            <DuplicateWarning matches={dupMatches} currency={currencyValue} />
                            {ocrConf && Object.values(ocrConf).some(v => v < CONFIDENCE_FLOOR) && (
                                <Alert color="warning" className="d-flex align-items-start gap-2 py-2 small">
                                    <BiErrorCircle className="mt-1 flex-shrink-0" />
                                    <div>
                                        Some fields were detected with low confidence. Please verify and save.
                                    </div>
                                </Alert>
                            )}
                            <Row>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Expense Date *</Label>
                                        <Input
                                            type="date"
                                            value={expenseDate}
                                            onChange={(e) => setExpenseDate(e.target.value)}
                                            invalid={attempted && !!errors.expenseDate}
                                        />
                                        <FormFeedback>{errors.expenseDate}</FormFeedback>
                                        {lowConfFeedback('expenseDate')}
                                    </FormGroup>
                                </Col>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Category</Label>
                                        <Input
                                            type="select"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            disabled={categories.isLoading}
                                            className={isLowConf(ocrConf, 'category') ? 'border-warning' : ''}
                                        >
                                            <option value="">— Select category —</option>
                                            {(categories.data ?? []).map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </Input>
                                        {lowConfFeedback('category')}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Supplier</Label>
                                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} />
                                {lowConfFeedback('supplier')}
                            </FormGroup>
                            <FormGroup>
                                <Label>Description</Label>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input
                                    type="select"
                                    value={currencyValue}
                                    onChange={(e) => setCurrencyValue(e.target.value)}
                                    className={isLowConf(ocrConf, 'currency') ? 'border-warning' : ''}
                                >
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                                    ))}
                                </Input>
                                {lowConfFeedback('currency')}
                            </FormGroup>
                            <Row>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>Amount *</Label>
                                        <InputGroup>
                                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                invalid={attempted && !!errors.amount}
                                            />
                                            <FormFeedback>{errors.amount}</FormFeedback>
                                        </InputGroup>
                                        {lowConfFeedback('amount')}
                                    </FormGroup>
                                </Col>
                                <Col xs={6}>
                                    <FormGroup>
                                        <Label>VAT Amount</Label>
                                        <InputGroup>
                                            <InputGroupText>{currencySymbol(currencyValue)}</InputGroupText>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={vatAmount}
                                                onChange={(e) => setVatAmount(e.target.value)}
                                                invalid={attempted && !!errors.vatAmount}
                                            />
                                            <FormFeedback>{errors.vatAmount}</FormFeedback>
                                        </InputGroup>
                                        {lowConfFeedback('vatAmount')}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <ExpenseSplitEditor
                                value={splits}
                                onChange={setSplits}
                                primaryJobId={expense?.jobId}
                                amount={amount}
                                vatAmount={vatAmount}
                                currency={currencyValue}
                                onAmountChange={setAmount}
                                disabled={updateExpense.isPending}
                            />
                            </fieldset>
                        </Col>
                    </Row>
                    {expense?.id && (
                        <div className="mt-3">
                            <AccountingSyncBadge type="Expense" id={expense.id} onStatusChange={(s) => setLocked(s?.status === 'locked')} />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={handleClose}
                            disabled={updateExpense.isPending || dupChecking}>
                        Cancel
                    </Button>
                    {!locked && (dupMatches.length > 0 ? (
                        <Button color="warning" type="button" onClick={doSave} disabled={updateExpense.isPending}>
                            {updateExpense.isPending && <Spinner size="sm" className="me-1" />}
                            Save anyway
                        </Button>
                    ) : (
                        <Button color="primary" type="submit" disabled={updateExpense.isPending || dupChecking}>
                            {(updateExpense.isPending || dupChecking) && <Spinner size="sm" className="me-1" />}
                            Save changes
                        </Button>
                    ))}
                </ModalFooter>
            </Form>
        </Modal>
    );
}
